import { useState, useEffect } from "react";
import { useAuth, authFetch } from "../App";
import { useTranslation } from "../i18n/LanguageProvider";
import { toast } from "sonner";
import {
  Navigation,
  Download,
  Filter,
  CheckCircle,
  XCircle,
  Search,
  ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = "/api";

export default function GpsAuditLog() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/app");
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logRes, usersRes] = await Promise.all([
        authFetch(`${API}/admin/gps-consent-log`),
        authFetch(`${API}/users`),
      ]);
      if (logRes.ok) setEntries(await logRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch {
      toast.error(t("common.fehler"));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterUser && filterUser !== "all") params.set("user_id", filterUser);
      if (filterFrom) params.set("from_date", filterFrom);
      if (filterTo) params.set("to_date", filterTo);
      const res = await authFetch(`${API}/admin/gps-consent-log?${params}`);
      if (res.ok) setEntries(await res.json());
    } catch {
      toast.error(t("common.fehler"));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterUser && filterUser !== "all") params.set("user_id", filterUser);
      if (filterFrom) params.set("from_date", filterFrom);
      if (filterTo) params.set("to_date", filterTo);
      const res = await authFetch(`${API}/admin/gps-consent-log/export?${params}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "gps_einwilligung_audit_log.csv";
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t("audit.export_success"));
      }
    } catch {
      toast.error(t("common.fehler"));
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setFilterUser("all");
    setFilterFrom("");
    setFilterTo("");
    loadData();
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const actionLabel = (action) => {
    if (action === "consent_given") return t("audit.action_given");
    if (action === "consent_revoked") return t("audit.action_revoked");
    return action;
  };

  const actionBadge = (action) => {
    if (action === "consent_given") {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid="audit-action-badge">
          <CheckCircle className="w-3 h-3" />
          {actionLabel(action)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="audit-action-badge">
        <XCircle className="w-3 h-3" />
        {actionLabel(action)}
      </span>
    );
  };

  // Summary stats
  const totalConsents = entries.filter((e) => e.action === "consent_given").length;
  const uniqueUsers = new Set(entries.map((e) => e.user_id)).size;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="gps-audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/app/mitarbeiter")}
            className="text-gray-400 hover:text-gray-600"
            data-testid="audit-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Navigation className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900" data-testid="audit-title">
              {t("audit.title")}
            </h1>
            <p className="text-sm text-gray-500">{t("audit.subtitle")}</p>
          </div>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting || entries.length === 0}
          variant="outline"
          className="gap-2"
          data-testid="audit-export-btn"
        >
          <Download className="w-4 h-4" />
          {t("audit.csv_export")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="audit-stat-total">
          <p className="text-xs text-gray-500 uppercase tracking-wider">{t("audit.total_consents")}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalConsents}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="audit-stat-users">
          <p className="text-xs text-gray-500 uppercase tracking-wider">{t("audit.unique_users")}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{uniqueUsers}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="audit-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{t("audit.filter")}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <label className="text-xs text-gray-500 block mb-1">{t("ts.mitarbeiter")}</label>
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="h-9" data-testid="audit-filter-user">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ts.alle")}</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("audit.from")}</label>
            <Input
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="h-9 w-40"
              data-testid="audit-filter-from"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("audit.to")}</label>
            <Input
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              className="h-9 w-40"
              data-testid="audit-filter-to"
            />
          </div>
          <Button onClick={applyFilters} size="sm" className="h-9 gap-1" data-testid="audit-filter-apply">
            <Search className="w-3.5 h-3.5" />
            {t("audit.apply_filter")}
          </Button>
          <Button onClick={resetFilters} variant="ghost" size="sm" className="h-9 text-gray-500" data-testid="audit-filter-reset">
            {t("audit.reset_filter")}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("emp.name")}
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("emp.email")}
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("audit.action")}
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("audit.timestamp")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-400">
                  {t("common.laden")}
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-400" data-testid="audit-empty">
                  {t("audit.no_entries")}
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                  data-testid="audit-row"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{entry.user_name}</td>
                  <td className="px-4 py-3 text-gray-600">{entry.user_email}</td>
                  <td className="px-4 py-3">{actionBadge(entry.action)}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                    {formatTimestamp(entry.timestamp)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer info */}
      {entries.length > 0 && (
        <p className="text-xs text-gray-400 mt-3 text-right" data-testid="audit-count">
          {entries.length} {t("audit.entries_shown")}
        </p>
      )}
    </div>
  );
}
